import { useEffect, useMemo, useState } from "react";
import type { SingleValue } from "react-select";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { TableWatermark } from "@/components/widgets/Revision/ui/components";
import { Screener } from "@/components/widgets/Screener";
import { Dropdown } from "@/components/widgets/Screener/ScreenerFilters/Filters/ui/Dropdown";
import { FilterDataProvider } from "@/context/FilterDataContext";
import { WidgetViewProvider } from "@/context/WidgetViewContext";
import type { Option } from "@/interfaces/Option";
import { cn } from "@/lib/utils";
import { Image } from "@/shared/components/Image";
import { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import { stockScannersState } from "../../../config";
import { getDeduplicatedFilters } from "../../../lib/helpers/getDeduplicatedFilters";
import type { StockScannerEvent, StockScannerUniverse } from "../../../types";
import {
	StockScannerShowcases,
	stockScannerEventConfig,
	stockScannerUniverseConfig,
} from "./stockScannerConfig";

const universeOptions: Option[] = Object.entries(
	stockScannerUniverseConfig,
).map(([key, config]) => ({
	label: config.label,
	value: key as StockScannerUniverse,
}));

const eventOptions: Option[] = Object.entries(stockScannerEventConfig).map(
	([key, config]) => ({
		label: config.label,
		value: key as StockScannerEvent,
	}),
);

const showcaseEventOptions: Option[] = StockScannerShowcases.map(
	(showcaseEvent) => {
		const config = stockScannerEventConfig[showcaseEvent];
		return {
			label: config.label,
			value: showcaseEvent,
		};
	},
);

type StockScannerItemProps = {
	id: string;
	universeValue: StockScannerUniverse;
	eventValue: StockScannerEvent;
	isAdvancedScanner?: boolean;
	onEventChange?: (eventValue: StockScannerEvent) => void;
	onStateChange?: OnStateChange;
	className?: string;
};

export const StockScannerItem = ({
	id,
	universeValue,
	eventValue,
	isAdvancedScanner = false,
	onEventChange,
	onStateChange,
	className,
}: StockScannerItemProps) => {
	const currentEventOptions = isAdvancedScanner
		? showcaseEventOptions
		: eventOptions;

	const [selectedUniverse, setSelectedUniverse] = useState<SingleValue<Option>>(
		universeOptions?.find((o) => o.value === universeValue) ??
			universeOptions[0],
	);
	const [selectedEvent, setSelectedEvent] = useState<SingleValue<Option>>(
		currentEventOptions?.find((o) => o.value === eventValue) ??
			currentEventOptions[0],
	);

	const mergedFilters = useMemo(() => {
		return getDeduplicatedFilters([
			...(stockScannerUniverseConfig[
				selectedUniverse?.value as StockScannerUniverse
			]?.filter ?? []),
			...(stockScannerEventConfig[selectedEvent?.value as StockScannerEvent]
				?.filter ?? []),
		]);
	}, [selectedEvent?.value, selectedUniverse?.value]);

	useEffect(() => {
		const eventOption = currentEventOptions?.find(
			(o) => o.value === eventValue,
		);
		if (eventOption) {
			setSelectedEvent(eventOption);
		}
	}, [eventValue, currentEventOptions]);

	const label =
		stockScannerEventConfig[selectedEvent?.value as StockScannerEvent]?.label;

	const text =
		stockScannerEventConfig[selectedEvent?.value as StockScannerEvent]?.text;

	const textParts =
		text
			?.split("\n")
			.map((line) => line.trim())
			.filter(Boolean) ?? [];

	const stockScannerImage =
		stockScannerEventConfig[selectedEvent?.value as StockScannerEvent]?.image;

	return (
		<div
			className={cn(className, "w-full flex flex-col gap-2")}
			data-testid="stock-scanner-item-component"
			id={id}
		>
			<h3 className="text-gold">{label}</h3>
			<div className="flex flex-col items-center gap-1 max-w-[280px]">
				<div className="flex justify-between items-center gap-3 w-full">
					<h4 className="text-light-gray">Event</h4>
					<Dropdown
						placeholder="Select Event"
						options={currentEventOptions}
						value={selectedEvent}
						onChange={(value) => {
							const option = value as SingleValue<Option>;
							setSelectedEvent(option);
							onEventChange?.(option?.value as StockScannerEvent);
						}}
						isTypeList={false}
						maxMenuHeight={190}
						className="w-[180px]"
					/>
				</div>

				<div className="flex justify-between items-center gap-3 w-full">
					<h4 className="text-light-gray">Universe</h4>
					<Dropdown
						placeholder="Select Universe"
						options={universeOptions}
						value={selectedUniverse}
						onChange={(option) =>
							setSelectedUniverse(option as SingleValue<Option>)
						}
						isTypeList={false}
						maxMenuHeight={190}
						className="w-[180px]"
					/>
				</div>
			</div>

			<div className="w-full flex flex-wrap gap-3 flex-1 @container">
				<div className="min-w-[220px] w-[55%] [@container(max-width:500px)]:w-full">
					<div className="h-[392px]">
						<WidgetViewProvider
							key={`${selectedUniverse?.value}-${selectedEvent?.value}`}
						>
							<FilterDataProvider>
								<Screener
									key={`${selectedUniverse?.value}-${selectedEvent?.value}`}
									state={{
										...stockScannersState,
										fields:
											stockScannerEventConfig[
												selectedEvent?.value as StockScannerEvent
											].fields,
										sort: stockScannerEventConfig[
											selectedEvent?.value as StockScannerEvent
										].sort,
										filter: mergedFilters,
									}}
									maxLimit={20}
									eventEmitter={new EventEmitter()}
									tileId={id}
									onStateChange={(state) => {
										if (state?.ticker) {
											onStateChange?.({ ticker: state.ticker });
										}
									}}
								/>
							</FilterDataProvider>
						</WidgetViewProvider>
					</div>
					<TableWatermark />
				</div>
				{(!!textParts?.length || stockScannerImage) && (
					<div className="flex-1 min-w-[220px] text-light-gray flex flex-col gap-2">
						{!!textParts?.length && (
							<div className="w-full">
								{textParts.map((textPart) => (
									<p key={textPart}>{textPart}</p>
								))}
							</div>
						)}
						{stockScannerImage && (
							<Image
								src={stockScannerImage}
								alt={label}
								loading="lazy"
								className="max-h-[300px] max-w-[70%] w-auto h-auto object-contain"
								width={150}
								height={150}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
